import { membershipTierRank } from "@/utils/memberTier";
import { hasTrialCooldownElapsed } from "./expiryEligibility";
import type { TrialGateInput } from "./types";
import {
  TRIAL_ACTIVE_SAME_TIER_BUTTON,
  TRIAL_ACTIVE_SAME_TIER_FOOTNOTE,
  TRIAL_DISPLAY_SKU,
  TRIAL_LOWER_BLOCKED_BUTTON,
  TRIAL_LOWER_BLOCKED_FOOTNOTE,
  TRIAL_QUEUE_PREFIX,
  TRIAL_UNAVAILABLE_MESSAGE,
  TRIAL_VERSION_SHORT,
  type PaidMemberPackageKey,
  type TrialOfferKind
} from "./constants";

export type { TrialGateInput } from "./types";

/** 跨档互斥：0 或目标档位值严格小于已购档位值。 */
export function canBuyTrial(targetTier: number, trialTier: number): boolean {
  return trialTier === 0 || targetTier < trialTier;
}

export function canShowTrialOffer(input: TrialGateInput): boolean {
  if (!input.monthSubscribe || input.sameTierActive) return false;
  if (membershipTierRank(input.activePackageKey) === 0 && input.expiryHistory && !hasTrialCooldownElapsed(input.expiryHistory)) return false;
  if (membershipTierRank(input.activePackageKey) > membershipTierRank(input.packageKey)) return false;
  return canBuyTrial(TRIAL_QUEUE_PREFIX[input.packageKey], input.trialTier);
}

/**
 * 尝鲜 Tab 卡片态（对齐原型 trialButtonState）：
 * - owned：已购/正在试用本档
 * - lower：低于已购试用最高档（或正在试用更高档）
 * - buy：可购买
 */
export function resolveTrialSurfaceState(input: TrialGateInput): "buy" | "owned" | "lower" {
  const targetRank = membershipTierRank(input.packageKey);
  const targetPrefix = TRIAL_QUEUE_PREFIX[input.packageKey];

  if (input.sameTierActive && input.currentActiveTrial) return "owned";
  if (input.trialTier > 0 && input.trialTier === targetPrefix) return "owned";

  const pastRank = purchasedTrialRank(input.trialTier);
  if (pastRank > 0 && targetRank < pastRank) return "lower";

  if (input.currentActiveTrial) {
    const activeRank = membershipTierRank(input.activePackageKey);
    if (activeRank > targetRank) return "lower";
  }

  return "buy";
}

/** trialTier：80=VIP、70=SVIP、60=VVIP → membership 阶梯 rank */
function purchasedTrialRank(trialTier: number): number {
  if (trialTier === 80) return membershipTierRank("vip");
  if (trialTier === 70) return membershipTierRank("svip");
  if (trialTier === 60) return membershipTierRank("ultra");
  return 0;
}

export type TrialCheckout = "blocked" | "refund-then-new" | "new" | "standard";

export interface TrialCheckoutResult {
  visible: boolean;
  checkout: TrialCheckout;
  /** undefined 表示不传 isTrial；false 必须显式传 */
  declaration: boolean | undefined;
  buttonText: string | null;
  hideCoupon: boolean;
  displayPriceYuan: number | null;
  credits: number | null;
  /** 覆盖默认续费脚注；同档正在试用时用已购提示 */
  footnote?: string | null;
}

export function resolveTrialCheckout(
  input: TrialGateInput & { offer: TrialOfferKind }
): TrialCheckoutResult {
  const sku = TRIAL_DISPLAY_SKU[input.packageKey];

  // 尝鲜试用选中：owned / lower 仍展示试用价 + 阻断文案（对齐原型图1）
  if (input.offer === "trial" && input.monthSubscribe) {
    const surface = resolveTrialSurfaceState(input);
    if (surface === "owned") {
      return blockedTrialFace(sku, TRIAL_ACTIVE_SAME_TIER_BUTTON, TRIAL_ACTIVE_SAME_TIER_FOOTNOTE);
    }
    if (surface === "lower") {
      return blockedTrialFace(sku, TRIAL_LOWER_BLOCKED_BUTTON, TRIAL_LOWER_BLOCKED_FOOTNOTE);
    }
  }

  // 普通完整版由 memberCard 矩阵裁决；本层只补充正在试用/选中试用的限制。
  // 否则会把合法的月买断转订阅、存量半月升月度一起挡住。
  if (input.sameTierActive && input.monthSubscribe && (input.currentActiveTrial || input.offer === "trial")) {
    return emptyCheckout(`您已是${TRIAL_VERSION_SHORT[input.packageKey]}会员`, "blocked");
  }

  const visible = canShowTrialOffer(input);
  if (input.offer === "trial" && input.monthSubscribe && !visible) {
    return emptyCheckout(TRIAL_UNAVAILABLE_MESSAGE, "blocked");
  }
  if (!visible || input.offer === "full") {
    return {
      ...emptyCheckout(null, "standard"),
      declaration: input.monthSubscribe ? false : undefined
    };
  }

  return newTrialCheckout(input);
}

function newTrialCheckout(input: TrialGateInput): TrialCheckoutResult {
  const sku = TRIAL_DISPLAY_SKU[input.packageKey];
  const shortName = TRIAL_VERSION_SHORT[input.packageKey];
  const lowerTierActive =
    membershipTierRank(input.activePackageKey) > 0 &&
    membershipTierRank(input.activePackageKey) < membershipTierRank(input.packageKey);
  // 在期低档（含正在试用）升更高档尝鲜：统一「升级为SVIP/VVIP试用」，走退款再新单
  return {
    visible: true,
    checkout: lowerTierActive ? "refund-then-new" : "new",
    declaration: true,
    buttonText: lowerTierActive ? `升级为${shortName}试用` : "立即订阅",
    hideCoupon: true,
    displayPriceYuan: sku.priceYuan,
    credits: sku.credits
  };
}

function blockedTrialFace(
  sku: (typeof TRIAL_DISPLAY_SKU)[PaidMemberPackageKey],
  buttonText: string,
  footnote: string
): TrialCheckoutResult {
  return {
    visible: false,
    checkout: "blocked",
    declaration: undefined,
    buttonText,
    hideCoupon: true,
    displayPriceYuan: sku.priceYuan,
    credits: sku.credits,
    footnote
  };
}

function emptyCheckout(buttonText: string | null, checkout: TrialCheckout): TrialCheckoutResult {
  return {
    visible: false,
    checkout,
    declaration: undefined,
    buttonText,
    hideCoupon: false,
    displayPriceYuan: null,
    credits: null
  };
}

export interface TrialPreorderOutcome {
  action: "pay" | "abort";
  amountMismatch: boolean;
  message?: string;
}

/** 以响应试用选中为准。声明了但没选中则中止；金额不一致只告警仍支付。 */
export function resolveTrialPreorderOutcome(input: {
  declared: boolean;
  selected: boolean;
  totalAmountFen?: number | null;
  displayPriceFen?: number | null;
}): TrialPreorderOutcome {
  if (input.declared && !input.selected) {
    return { action: "abort", amountMismatch: false, message: TRIAL_UNAVAILABLE_MESSAGE };
  }
  const mismatch =
    input.declared &&
    input.selected &&
    input.displayPriceFen != null &&
    input.totalAmountFen !== input.displayPriceFen;
  return { action: "pay", amountMismatch: mismatch };
}

export class TrialOfferUnavailableError extends Error {
  override name = "TrialOfferUnavailableError";

  constructor(message = TRIAL_UNAVAILABLE_MESSAGE) {
    super(message);
  }
}
